import { useEffect, useMemo, useRef, useState } from "react";
import { useLocalization } from "../../contexts/LocalizationContext";
import styles from "./ZeqiLoadingModal.module.css";

const clamp = (value, min = 0, max = 100) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return min;
  return Math.max(min, Math.min(max, num));
};

const ZeqiLoadingModal = ({ visible = true, progress = 0, onClose, status }) => {
  const { t, locale } = useLocalization();
  const isArabic = locale === "ar";
  const target = clamp(progress);
  const [displayPct, setDisplayPct] = useState(0);
  const lastTargetRef = useRef(0);

  useEffect(() => {
    if (!visible) return;
    let raf = null;
    const start = lastTargetRef.current;
    let startTime = null;
    const delta = target - start;
    const duration = Math.max(400, Math.min(1600, Math.abs(delta) * 20));

    const tick = (time) => {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const pct = Math.min(1, elapsed / duration);
      setDisplayPct(Math.round(start + delta * pct));
      if (pct < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, visible]);

  useEffect(() => {
    if (!visible) return;
    if (target >= 100) {
      const timeout = setTimeout(() => onClose?.(), 600);
      return () => clearTimeout(timeout);
    }
  }, [target, visible, onClose]);

  useEffect(() => {
    lastTargetRef.current = target;
  }, [target]);

  const ringStyle = useMemo(() => {
    const deg = target * 3.6;
    return {
      background: `conic-gradient(#2d6bff 0deg, #6aa3ff ${deg}deg, #ecf3ff ${deg}deg)`
    };
  }, [target]);

  if (!visible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.sheet}>
        <button className={styles.closeButton} onClick={onClose} type="button">
          x
        </button>
        <div className={styles.handle} />
        <h3 className={`${styles.title} ${isArabic ? styles.rtl : ""}`}>
          {t("ZeqiLoadingModal.title") || "Creating Your Zeqi Profile"}
        </h3>
        <p className={`${styles.subtitle} ${isArabic ? styles.rtl : ""}`}>
          {t("ZeqiLoadingModal.subtitle") ||
            "This helps us figure out what is wrong and fix it using our smart AI tools."}
        </p>

        <div className={styles.gaugeWrap}>
          <div className={styles.ring} style={ringStyle}>
            <div className={styles.gaugeInner}>
              <span className={styles.gaugeText}>{displayPct}%</span>
            </div>
          </div>
        </div>

        <p className={`${styles.status} ${isArabic ? styles.rtl : ""}`}>
          {status || t("ZeqiLoadingModal.status") || "Gathering Information From Shopify"}
        </p>
      </div>
    </div>
  );
};

export default ZeqiLoadingModal;
